import { useCallback, useEffect, useRef } from "react";

import InputError from "@/components/input-error";

type TurnstileWidgetId = string | number;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          action?: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          size?: "normal" | "compact" | "flexible";
          appearance?: "always" | "execute" | "interaction-only";
          theme?: "auto" | "light" | "dark";
        },
      ) => TurnstileWidgetId;
      remove?: (widgetId: TurnstileWidgetId) => void;
    };
  }
}

interface TurnstileWidgetProps {
  siteKey: string;
  action: string;
  onTokenChange: (token: string) => void;
  error?: string;
}

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function TurnstileWidget({ siteKey, action, onTokenChange, error }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<TurnstileWidgetId | null>(null);

  const renderWidget = useCallback(() => {
    if (!window.turnstile || !containerRef.current) return;

    if (widgetIdRef.current !== null && window.turnstile.remove) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }

    containerRef.current.innerHTML = "";
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      action,
      callback: (token) => onTokenChange(token),
      "expired-callback": () => onTokenChange(""),
      "error-callback": () => onTokenChange(""),
      size: "flexible",
      appearance: "always",
      theme: "auto",
    });
  }, [action, onTokenChange, siteKey]);

  useEffect(() => {
    onTokenChange("");
    if (typeof window === "undefined" || !siteKey || !containerRef.current) return;

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existingScript || document.createElement("script");

    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const handleLoad = () => renderWidget();
    script.addEventListener("load", handleLoad);

    return () => {
      script.removeEventListener("load", handleLoad);

      if (widgetIdRef.current !== null && window.turnstile?.remove) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onTokenChange, renderWidget, siteKey]);

  if (!siteKey) return null;

  return (
    <div className="grid gap-2">
      <div ref={containerRef} className="w-full" />
      <InputError message={error} />
    </div>
  );
}
